import { validateName } from '@paskills/validation';
import { TextInput } from 'lego-on-react';
import block from 'propmods';
import React from 'react';
import { WithSnapshot, withSnapshot } from '../../../context/snapshotContext';
import formContainer from '../../../hocs/form-container';
import { InjectedFormProps } from '../../../hocs/types';
import { updateDraft } from '../../../api';
import { ApiError } from '../../../api/error';
import { ThereminvoxSettings } from '../../../model/settings';
import { Skill } from '../../../model/skill';
import { ThereminvoxFormSettings } from '../../../types';
import { makeErrorsSameShapeAsValues } from '../../../utils/settings/common';
import { Field, Header, CheckboxField, FormCheckbox } from '../../Form/Form';
import Submit from '../Submit';
import { thereminvoxLabels as l } from './labels';
import * as utils from './utils';
import { mapThereminvoxSettingsToValues, mapValuesToThereminvoxSettings } from '../../../utils/settings/thereminvox';
import { thereminvoxNameDescription, thereminvoxHideInStoreDescription } from './descriptions';
import { ThereminvoxSounds } from '../../ThereminnvoxSounds/ThereminvoxSounds';
import * as keyset from 'i18n/Thereminvox';
import i18n from '@yandex-int/i18n';
import './ThereminvoxForm.scss';

const t = i18n(keyset);

interface OuterProps extends WithSnapshot {
    skill: Skill;
    disabled: boolean;
    settings: ThereminvoxSettings;
}

const b = block('ThereminvoxForm');

type Props = InjectedFormProps<ThereminvoxFormSettings, OuterProps>;

const ThereminvoxFormFields: React.FC<Props> = (props) => {
    const { values, isSubmitting, errors, onChange, onCheckboxChange, isValid, isNonValidationError, skill } = props;

    const submitSeverity = !isValid || isNonValidationError ? 'error' : 'info';
    const submitMessage = isNonValidationError ? t('error') : isValid ? '' : t('error-form');

    return (
        <div {...b()}>
            <Header>{t('header-settings')}</Header>
            <Field name="name" required label={l.name} error={errors.name} description={thereminvoxNameDescription}>
                <TextInput
                    theme="normal"
                    size="s"
                    text={values.name}
                    onChange={onChange('name')}
                    disabled={props.disabled}
                    autocomplete={false}
                />
            </Field>
            <CheckboxField
                label={l.hideInStore}
                name="hideInStore"
                checked={values.hideInStore}
                onChange={onCheckboxChange('hideInStore')}
                disabled // Пока создаем только приватные
                description={thereminvoxHideInStoreDescription}
                htmlTitle={t('description-private-only')}
            >
                {t('checkbox-private')}
            </CheckboxField>
            <Header>{t('header-sounds')}</Header>
            <ThereminvoxSounds
                skill={skill}
                soundsMeta={values.soundsMeta}
                onSoundsMetaChange={onChange('soundsMeta')}
                disabled={props.disabled}
                noOverlaySamples={values.noOverlaySamples}
                repeatSoundInside={values.repeatSoundInside}
                stopOnCeil={values.stopOnCeil}
            />
            <Field label={t('label-dial-settings')}>
                <div {...b('soundset-settings')}>
                    <div {...b('soundset-checkbox')}>
                        <FormCheckbox
                            checked={values.repeatSoundInside}
                            disabled={props.disabled}
                            onChange={onCheckboxChange('repeatSoundInside')}
                            multiline
                        >
                            {t('dial-option-repeat')}
                        </FormCheckbox>
                    </div>
                    <div {...b('soundset-checkbox')}>
                        <FormCheckbox
                            checked={values.noOverlaySamples}
                            disabled={props.disabled}
                            onChange={onCheckboxChange('noOverlaySamples')}
                            multiline
                        >
                            {t('dial-option-not-play')}
                        </FormCheckbox>
                    </div>
                    <div {...b('soundset-checkbox')}>
                        <FormCheckbox
                            checked={values.stopOnCeil}
                            disabled={props.disabled}
                            onChange={onCheckboxChange('stopOnCeil')}
                            multiline
                        >
                            {t('dial-option-stop')}
                        </FormCheckbox>
                    </div>
                </div>
            </Field>
            {!props.disabled && (
                <>
                    <Submit
                        text={t('button-save')}
                        inProgress={isSubmitting}
                        disabled={!isValid}
                        message={submitMessage}
                        severity={submitSeverity}
                    />
                </>
            )}
        </div>
    );
};

const ThereminvoxFormComp = formContainer<OuterProps, ThereminvoxFormSettings>({
    mapPropsToValues: (props) => mapThereminvoxSettingsToValues(props.settings),
    handleSubmit: async (values, props, { setErrors }) => {
        try {
            const settings = mapValuesToThereminvoxSettings(values);

            await updateDraft(props.skill.id, settings);
            await props.refreshSnapshot();
        } catch (err) {
            if (err instanceof ApiError) {
                if (err.fields) {
                    // С сервера прилетают ошибки с полей, которых нет в форме чатов, таких как activationPhrases
                    const errors = makeErrorsSameShapeAsValues(err.fields, values);

                    // @ts-expect-error legacy
                    await new Promise((resolve) => setErrors(errors, resolve));
                }
            } else {
                console.error(err);
            }

            throw err;
        }
    },
    setupValidationSchema: (props) => {
        const validateAsAdmin = utils.validateAsAdmin(__appConfig.user.isAdmin, props.skill.developerType);

        return {
            name: (value: string) => ({
                name: validateName(value, validateAsAdmin, 'Название'),
            }),
        };
    },
})(ThereminvoxFormFields);

export const ThereminvoxForm = withSnapshot(ThereminvoxFormComp);
